/**
 * Sticky Add to Cart — shows after the main buy box scrolls out of view.
 * Hides (fade) when the footer enters the bottom sticky zone.
 * Triggers the primary product form submit button via [data-sticky-atc-button].
 */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('[data-sticky-atc-bar]');
    this.buttons = this.querySelectorAll('[data-sticky-atc-button]');
    this.priceEl = this.querySelector('[data-sticky-atc-price]');
    this.sentinelSelector =
      this.dataset.sentinel ||
      '[data-sticky-atc-sentinel], .buy-buttons-block, product-form-component, .product-details .add-to-cart-button';
    this.footerSelector = this.dataset.footer || '.beyond-footer';
    this._pastBuyBox = false;
    this._footerBlocking = false;

    this._onClick = this._onClick.bind(this);
    this._onPriceUpdate = this._onPriceUpdate.bind(this);
    this._onVariantUpdate = this._onVariantUpdate.bind(this);
    this._onFooterScroll = this._onFooterScroll.bind(this);

    this.buttons.forEach((btn) => btn.addEventListener('click', this._onClick));
    document.addEventListener('variant:update', this._onVariantUpdate);
    document.addEventListener('price:update', this._onPriceUpdate);

    this._syncLabel();
    this._observe();
  }

  disconnectedCallback() {
    this.buttons?.forEach((btn) => btn.removeEventListener('click', this._onClick));
    document.removeEventListener('variant:update', this._onVariantUpdate);
    document.removeEventListener('price:update', this._onPriceUpdate);
    this._observer?.disconnect();
    this._footerObserver?.disconnect();
    window.removeEventListener('scroll', this._onFooterScroll);
    window.removeEventListener('resize', this._onFooterScroll);
    if (this._onScroll) {
      window.removeEventListener('scroll', this._onScroll);
    }
  }

  _syncVisibility() {
    const show = this._pastBuyBox && !this._footerBlocking;
    this.classList.toggle('is-visible', show);
    this.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  _footerClearance() {
    const barHeight = this.bar?.getBoundingClientRect().height || 80;
    return Math.round(barHeight + 40);
  }

  _isFooterBlocking() {
    const footer = document.querySelector(this.footerSelector);
    if (!footer) return false;
    const rect = footer.getBoundingClientRect();
    const clearance = this._footerClearance();
    // Footer overlaps the bottom sticky zone (where the ATC bar sits)
    return rect.top < window.innerHeight - clearance && rect.bottom > window.innerHeight - clearance;
  }

  _onFooterScroll() {
    this._footerBlocking = this._isFooterBlocking();
    this._syncVisibility();
  }

  _observeFooter() {
    const footer = document.querySelector(this.footerSelector);
    if (!footer) return;

    const clearance = this._footerClearance();

    if (typeof IntersectionObserver !== 'undefined') {
      this._footerObserver = new IntersectionObserver(
        () => {
          this._footerBlocking = this._isFooterBlocking();
          this._syncVisibility();
        },
        { threshold: [0, 0.01, 0.1, 0.25, 0.5, 1], rootMargin: `0px 0px -${clearance}px 0px` }
      );
      this._footerObserver.observe(footer);
    }

    // Scroll/resize backup — IO can miss some programmatic or edge scroll positions
    window.addEventListener('scroll', this._onFooterScroll, { passive: true });
    window.addEventListener('resize', this._onFooterScroll, { passive: true });
    this._onFooterScroll();
  }

  _observe() {
    this._observeFooter();

    const sentinel = document.querySelector(this.sentinelSelector);
    if (!sentinel || typeof IntersectionObserver === 'undefined') {
      this._onScroll = () => {
        this._pastBuyBox = window.scrollY > window.innerHeight * 0.55;
        this._footerBlocking = this._isFooterBlocking();
        this._syncVisibility();
      };
      window.addEventListener('scroll', this._onScroll, { passive: true });
      this._onScroll();
      return;
    }

    this._observer = new IntersectionObserver(
      ([entry]) => {
        this._pastBuyBox = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this._syncVisibility();
      },
      { threshold: 0, rootMargin: '0px' }
    );
    this._observer.observe(sentinel);
  }

  _labelSource(event) {
    const fromEvent = event?.detail?.data?.html?.querySelector?.(
      '.product-details [data-atc-label]'
    );
    return (
      fromEvent ||
      document.querySelector('.product-details [data-atc-label]')
    );
  }

  _syncLabel(event) {
    const label = this._labelSource(event)?.getAttribute('data-atc-label')?.trim();
    if (!label) return;
    this.buttons?.forEach((btn) => {
      btn.textContent = label;
    });
  }

  _onVariantUpdate(event) {
    this._onPriceUpdate(event);
    this._syncLabel(event);
  }

  _onClick(event) {
    event.preventDefault();
    const formButton =
      document.querySelector(
        'product-form-component [type="submit"].add-to-cart-button, product-form-component button[name="add"], .product-details .add-to-cart-button'
      ) || document.querySelector('form[action*="/cart/add"] [type="submit"]');

    if (formButton) {
      formButton.click();
      return;
    }

    const form = document.querySelector('product-form-component form, form[action*="/cart/add"]');
    form?.requestSubmit?.() || form?.submit?.();
  }

  _onPriceUpdate(event) {
    if (!this.priceEl) return;
    const price =
      event?.detail?.price ||
      event?.detail?.priceHTML ||
      document.querySelector('.product-details .price, .price-item--sale, .price__regular')?.textContent;
    if (typeof price === 'string' && price.trim()) {
      this.priceEl.textContent = price.trim();
    }
  }
}

if (!customElements.get('sticky-atc')) {
  customElements.define('sticky-atc', StickyAtc);
}
